import { useEffect, useState } from 'react';

/**
 * Looping, non-interactive marketing demo of the KDP walkthrough page.
 * Mirrors the real KdpWalkthrough layout (header + two-column step grid
 * with sidebar). One step's checklist animates as items get checked off,
 * then completes and advances to the next step. Decorative only.
 */

interface Step {
  key: string;
  icon: string;
  title: string;
  body: string;
  items: string[];
}

const STEPS: Step[] = [
  {
    key: 'account',
    icon: '👤',
    title: 'Create your KDP account',
    body: 'Go to kdp.amazon.com and sign in. Complete tax info and add your bank account for royalty payments.',
    items: [
      'Visit kdp.amazon.com',
      'Complete tax interview (W-9 or W-8BEN)',
      'Add bank account for royalties',
    ],
  },
  {
    key: 'metadata',
    icon: '📝',
    title: 'Add book metadata',
    body: 'From your KDP Bookshelf, click + Kindle eBook. Fill in title, author, description, 2 categories, and 7 keywords.',
    items: [
      'Title and author name',
      'Book description (100–200 words)',
      '2 categories + 7 keywords',
    ],
  },
  {
    key: 'cover',
    icon: '🎨',
    title: 'Upload your cover',
    body: 'Export your cover from StoryMint and upload it to KDP. Minimum 1000 × 1600 px, JPG or TIFF, sRGB.',
    items: [
      'Export cover from StoryMint',
      'Upload to KDP Content tab',
      'Preview in KDP cover viewer',
    ],
  },
];

const TOTAL_PHASES = 5; // 0..3 = items checking off on step 2; 4 = step 3 active; loops
const PHASE_MS = 1400;

type StepStatus = 'complete' | 'active' | 'pending';

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setPrefers(mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return prefers;
}

function BrowserChrome({ url }: { url: string }) {
  return (
    <div
      className="h-[38px] border-b border-border flex items-center px-4 gap-1.5 shrink-0"
      style={{ background: '#16161f' }}
    >
      <span className="w-3 h-3 rounded-full bg-[#ff5f57] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#28c840] shrink-0" />
      <div
        className="flex-1 max-w-[360px] mx-auto h-[22px] rounded flex items-center justify-center text-[11px] text-muted-foreground font-mono px-3"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        {url}
      </div>
    </div>
  );
}

function StepCircle({ status, icon }: { status: StepStatus; icon: string }) {
  if (status === 'complete') {
    return (
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-green-500 bg-green-500/15 text-green-400 shrink-0 transition-colors duration-500">
        ✓
      </div>
    );
  }
  if (status === 'active') {
    return (
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-primary bg-primary/15 text-primary shrink-0 transition-colors duration-500">
        {icon}
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2 border-border bg-card text-muted-foreground shrink-0 transition-colors duration-500">
      {icon}
    </div>
  );
}

function StatusBadge({ status }: { status: StepStatus }) {
  if (status === 'complete') {
    return (
      <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-400 font-medium shrink-0 transition-colors duration-500">
        Done
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-primary/15 text-[#a78bfa] font-medium shrink-0 transition-colors duration-500">
        Up next
      </span>
    );
  }
  return (
    <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 font-medium shrink-0 transition-colors duration-500">
      Pending
    </span>
  );
}

export function KdpWalkthroughDemo() {
  const reducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = useState(reducedMotion ? TOTAL_PHASES - 1 : 0);

  useEffect(() => {
    if (reducedMotion) {
      setPhase(TOTAL_PHASES - 1);
      return;
    }
    const id = setInterval(() => {
      setPhase((p) => (p + 1) % TOTAL_PHASES);
    }, PHASE_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  // Step 1 is always complete. Step 2 cycles. Step 3 follows.
  const getStatus = (idx: number): StepStatus => {
    if (idx === 0) return 'complete';
    if (idx === 1) {
      // active while items are filling in (phases 0..3); complete on phase 4
      return phase >= 4 ? 'complete' : 'active';
    }
    // idx === 2
    return phase >= 4 ? 'active' : 'pending';
  };

  // For step 2's items, how many are checked in this phase?
  const step2CheckedCount = Math.min(phase, STEPS[1]!.items.length);
  // Step 1's items are always all checked
  const isItemChecked = (stepIdx: number, itemIdx: number): boolean => {
    if (stepIdx === 0) return true;
    if (stepIdx === 1) return itemIdx < step2CheckedCount || phase >= 4;
    return false;
  };

  return (
    <div
      role="img"
      aria-label="StoryMint KDP walkthrough showing publishing steps with one in progress"
      className="rounded-xl overflow-hidden border border-border mb-12"
      style={{ background: '#0d0d12', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
    >
      <BrowserChrome url="app.storymint.io/kdp-walkthrough" />

      <div
        className="relative w-full overflow-hidden"
        style={{
          height: 'clamp(620px, 70vw, 720px)',
          background: 'hsl(var(--background))',
        }}
        aria-hidden="true"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          {/* Page header */}
          <div className="mb-5">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 mb-2">
              ← Back to My Books
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-1">
              Publish to Amazon KDP
            </h1>
            <p className="text-muted-foreground text-xs sm:text-sm max-w-xl">
              Follow these steps to get your finished book live on Amazon Kindle.
              Takes about 15 minutes the first time.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-[1fr_280px] gap-5 items-start">
            {/* LEFT: step checklist */}
            <div className="space-y-0">
              {STEPS.map((step, idx) => {
                const status = getStatus(idx);
                const isLast = idx === STEPS.length - 1;

                return (
                  <div key={step.key} className="flex items-start gap-3">
                    {/* Circle + connector */}
                    <div className="flex flex-col items-center shrink-0">
                      <StepCircle status={status} icon={step.icon} />
                      {!isLast && (
                        <div
                          className={`w-0.5 flex-1 min-h-[24px] mt-1 transition-colors duration-500 ${
                            status === 'complete' ? 'bg-green-500/35' : 'bg-border'
                          }`}
                        />
                      )}
                    </div>

                    {/* Step card */}
                    <div className="flex-1 pb-3">
                      <div
                        className={`border rounded-xl bg-card p-4 transition-colors duration-500 ${
                          status === 'active' ? 'border-primary/35' : 'border-border'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-primary">
                            Step {idx + 1}
                          </span>
                          <h2 className="text-sm font-semibold text-foreground flex-1 leading-snug">
                            {step.title}
                          </h2>
                          <StatusBadge status={status} />
                        </div>

                        <div className="text-[12px] text-muted-foreground leading-relaxed">
                          <p className="mb-2 line-clamp-2">{step.body}</p>
                          <ul className="space-y-1.5">
                            {step.items.map((label, iIdx) => {
                              const checked = isItemChecked(idx, iIdx);
                              return (
                                <li key={iIdx} className="flex items-start gap-2 text-[12px]">
                                  <span
                                    className={`mt-0.5 shrink-0 text-sm leading-none transition-colors duration-500 ${
                                      checked ? 'text-green-400' : 'text-muted-foreground/70'
                                    }`}
                                  >
                                    {checked ? '✓' : '○'}
                                  </span>
                                  <span
                                    className={`transition-all duration-500 ${
                                      checked ? 'line-through opacity-60' : ''
                                    }`}
                                  >
                                    {label}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT: book preview sidebar */}
            <div className="flex flex-col gap-4">
              <div className="border border-border rounded-xl bg-card p-4">
                <h3 className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                  Your Book
                </h3>

                {/* Cover */}
                <div
                  className="w-full rounded-lg border border-border mb-3 flex flex-col items-center justify-center relative overflow-hidden"
                  style={{
                    aspectRatio: '6/9',
                    maxHeight: '220px',
                    background:
                      'linear-gradient(160deg, #2a1850 0%, #1a1a2e 55%, #0d0d12 100%)',
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-lg"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(135deg, transparent, transparent 12px, rgba(124,58,237,0.04) 12px, rgba(124,58,237,0.04) 13px)',
                    }}
                  />
                  <div className="relative text-center px-4">
                    <span className="text-2xl mb-2 inline-block" aria-hidden="true">
                      🗺️
                    </span>
                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1">
                      A Novel
                    </p>
                    <p className="text-xs font-bold text-foreground leading-tight">
                      The Drawn City
                    </p>
                  </div>
                </div>

                {/* Book meta */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Genre</span>
                    <span className="font-medium capitalize">Fantasy</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Word count</span>
                    <span className="font-medium">51,420 words</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Chapters</span>
                    <span className="font-medium">20 chapters</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-medium text-green-400">✓ Complete</span>
                  </div>
                </div>
              </div>

              {/* Download card */}
              <div className="border border-border rounded-xl bg-card p-4">
                <h3 className="text-xs font-semibold text-foreground mb-3">
                  Download Your Manuscript
                </h3>
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <div className="flex items-center gap-2">
                    <span className="text-base">📘</span>
                    <div>
                      <p className="text-[12px] font-medium text-foreground leading-tight">EPUB</p>
                      <p className="text-[10px] text-muted-foreground">For Kindle</p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 bg-primary text-primary-foreground text-[10px] font-semibold rounded-md">
                    Download
                  </span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-base">📄</span>
                    <div>
                      <p className="text-[12px] font-medium text-foreground leading-tight">DOCX</p>
                      <p className="text-[10px] text-muted-foreground">Print-ready</p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 border border-border text-foreground text-[10px] font-semibold rounded-md">
                    Download
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
